/**
 * The country this player app instance belongs to.
 *
 * Country is decided by the domain the player arrived on, never by anything the
 * player types — see doc/MULTI_COUNTRY_ROLLOUT_CHECKLIST.md §1.1.
 *
 * `GET /api/v1/countries` (CountryController) serves the active rows of
 * `country_registry`. It takes no parameters, so it is a *discovery list* — it
 * cannot say which country the current domain is. See `countryStore` for how the
 * active country is inferred from that list and why the inference is deliberately
 * unwilling to guess.
 */

export type CountryConfig = {
  /** ISO-3166-1 alpha-2, matching `country_registry.code`. */
  code: string;
  /** Endonym, resolved server-side from `name_i18n[default_locale]` (checklist §3.6 C). */
  name: string;
  /** Full `name_i18n` map, for the main-domain picker which renders every country at once. */
  nameI18n: Record<string, string>;
  /** `country_registry.fiat_currency`. */
  fiatCurrency: string;
  /** `country_registry.timezone` — business-day boundaries, not player-facing clocks. */
  timezone: string;
  /**
   * E.164 calling code without the `+`, from `country_registry.phone_prefix`
   * (V207). Digits only — the app renders the `+` itself.
   */
  phonePrefix: string;
  /** `country_registry.default_locale`. */
  defaultLocale: string;
  /**
   * The languages this country exposes, from `country_registry.available_locales`
   * (V207) — normally a subset of what the bundle ships. The DB guarantees a
   * non-empty array containing `defaultLocale`.
   *
   * Empty means *not told* (a backend older than V207), which is unconstrained
   * rather than "no languages" — see {@link offeredLanguages}.
   */
  availableLocales: string[];
  /**
   * Decimals the market actually shows on its fiat, from
   * `country_registry.fiat_display_decimals` (V310). Null means the currency's
   * ISO minor units, which is what `Intl` prints on its own.
   *
   * Argentina is 0: ISO says ARS has centavos, Argentines stopped writing them
   * years ago, and `$ 18.093.000,00` is two characters of noise on every
   * amount. The formatter cannot learn that from the currency code, so the
   * registry says it — one number per country, no code change to open a market.
   */
  fiatDisplayDecimals: number | null;
  /**
   * The symbol printed in front of fiat amounts, from
   * `country_registry.fiat_symbol` (V311), or null for the one `Intl` prints.
   *
   * Argentina is `AR$`: es-AR renders ARS as a bare `$`, which at home is
   * unambiguous but on a product that also pays USDT is one character from
   * looking like dollars. Product call, 2026-09-15. Brazil stays null → `R$`.
   */
  fiatSymbol: string | null;
};

/**
 * Brazil, the only live country today, and the shape every screen falls back to.
 *
 * Falling back to a concrete country rather than to `null` keeps the auth screens
 * renderable when the lookup fails. The cost is that a broken lookup looks like
 * Brazil, which is why `countryStore` tracks whether it actually got an answer
 * and the registration notice surfaces that separately.
 */
export const BR_FALLBACK: CountryConfig = {
  code: 'BR',
  name: 'Brasil',
  nameI18n: { en: 'Brazil', pt: 'Brasil', es: 'Brasil' },
  fiatCurrency: 'BRL',
  timezone: 'America/Sao_Paulo',
  phonePrefix: '55',
  defaultLocale: 'pt-BR',
  availableLocales: ['pt-BR', 'en', 'es'],
  fiatDisplayDecimals: null,
  fiatSymbol: null,
};

/**
 * The languages to offer: the country's `available_locales` narrowed to what the
 * bundle can actually render.
 *
 * Both halves are needed. The registry does not know which locale chunks ship, so
 * a market configured for `hi` before Hindi is bundled would otherwise put a pill
 * on screen that loads nothing. And the bundle does not know which languages a
 * market wants, so it cannot narrow the list on its own.
 *
 * Order follows the country's list, since that is operator-chosen. Matching is
 * exact first, then by language subtag, so `es-AR` resolves to a shipped `es`.
 *
 * An **empty** `availableLocales` means the backend never told us — it predates
 * V207 — and that is unconstrained, not "no languages". Narrowing to the default
 * locale there would silently drop the EN and ES pills Brazilian players already
 * have, during exactly the window where the new frontend is live and the migration
 * is not. So empty offers everything the bundle ships.
 *
 * Never returns empty either way: an empty picker would leave a player no way to
 * change language at all, and the register screen is the one place they cannot
 * route around it.
 */
export function offeredLanguages<T extends string>(
  availableLocales: string[],
  shipped: readonly T[],
  fallback: T,
): T[] {
  if (!availableLocales.length) return [...shipped];

  const out: T[] = [];
  for (const wanted of availableLocales) {
    const exact = shipped.find((code) => code === wanted);
    const bySubtag = exact ?? shipped.find((code) => subtag(code) === subtag(wanted));
    if (bySubtag && !out.includes(bySubtag)) out.push(bySubtag);
  }
  return out.length ? out : [fallback];
}

/**
 * The language a player should land in when the country changes and their
 * current language is not on offer.
 *
 * The country's `default_locale`, resolved through {@link offeredLanguages} so it
 * is something the bundle can actually render (`es-AR` → `es`). Only when the
 * default itself is unshippable does this fall to the first offered language.
 *
 * Taking `offered[0]` directly was wrong (D-01, 2026-09-14): Argentina lists
 * `['en', 'es']` with default `es`, and a Portuguese phone switching to Argentina
 * landed in English. The order of `available_locales` is the pill order, not a
 * preference ranking — the preference is `default_locale`.
 */
export function landingLanguage<T extends string>(
  availableLocales: string[],
  defaultLocale: string,
  shipped: readonly T[],
  fallback: T,
): T {
  const offered = offeredLanguages(availableLocales, shipped, fallback);
  const wanted = offeredLanguages([defaultLocale], offered, offered[0]);
  return wanted[0];
}

function subtag(locale: string): string {
  const separator = locale.indexOf('-');
  return (separator > 0 ? locale.slice(0, separator) : locale).toLowerCase();
}

/** Digits-only national number, with the country's calling code stripped if present. */
export function nationalDigits(raw: string, country: CountryConfig): string {
  const digits = String(raw || '').replace(/\D/g, '');
  const prefix = country.phonePrefix;
  return prefix && digits.startsWith(prefix) ? digits.slice(prefix.length) : digits;
}

/**
 * The `phone` value the auth API expects: calling code + national number.
 *
 * Previously `55` was inlined at four call sites (two payload builders and two
 * country-code chips). Routing every one of them through here means adding a
 * country never means hunting for a literal.
 */
export function e164Digits(raw: string, country: CountryConfig): string {
  const national = nationalDigits(raw, country);
  return national ? `${country.phonePrefix}${national}` : '';
}

/** Parse one `items[]` entry from `GET /api/v1/countries`. */
export function parseCountryConfig(input: unknown): CountryConfig | null {
  if (!input || typeof input !== 'object') return null;
  const row = input as Record<string, unknown>;
  const code = text(row.code).toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;

  const defaultLocale = text(row.defaultLocale) || BR_FALLBACK.defaultLocale;
  const nameI18n = textMap(row.nameI18n);
  const locales = Array.isArray(row.availableLocales)
    ? row.availableLocales.map(text).filter(Boolean)
    : [];

  return {
    code,
    // The server already resolves the endonym; `nameI18n` is the fallback path
    // for a row that predates that resolution.
    name: text(row.name) || nameI18n[defaultLocale] || nameI18n.en || code,
    nameI18n,
    fiatCurrency: text(row.fiatCurrency).toUpperCase() || BR_FALLBACK.fiatCurrency,
    timezone: text(row.timezone) || BR_FALLBACK.timezone,
    // No client-side dial-code table: `phone_prefix` is NOT NULL in the registry,
    // so a missing value means an old backend, and only Brazil can be mid-rollout
    // today. Guessing a prefix for any other country would build a plausible but
    // wrong E.164 number, which is worse than an obviously empty field.
    phonePrefix:
      text(row.phonePrefix).replace(/\D/g, '')
      || (code === BR_FALLBACK.code ? BR_FALLBACK.phonePrefix : ''),
    defaultLocale,
    // Left empty when the field is absent, which `offeredLanguages` reads as
    // "unconstrained" rather than "only the default" — see the note there about
    // the pre-V207 rollout window.
    availableLocales: locales,
    fiatDisplayDecimals: displayDecimals(row.fiatDisplayDecimals),
    fiatSymbol: symbol(row.fiatSymbol),
  };
}

/**
 * A small whole number of decimals, or null. Anything else (absent, a string,
 * a negative, 2.5) is treated as "not told", which falls back to the currency's
 * ISO minor units — the same thing an older backend without V310 gives.
 */
function displayDecimals(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value)) return null;
  return value >= 0 && value <= 4 ? value : null;
}

/** A short printable symbol, or null. Whitespace inside would double up with the app's own spacing. */
function symbol(value: unknown): string | null {
  const trimmed = text(value);
  return /^\S{1,8}$/.test(trimmed) ? trimmed : null;
}

/** Parse the `items` envelope, dropping entries the contract cannot vouch for. */
export function parseCountryList(payload: unknown): CountryConfig[] {
  const items = (payload as { items?: unknown })?.items;
  if (!Array.isArray(items)) return [];
  return items.map(parseCountryConfig).filter((row): row is CountryConfig => row !== null);
}

/**
 * The country the auth screens should act as.
 *
 * One domain serves every country, so nothing in the request *decides* this. IP
 * only *suggests*: `CF-IPCountry` picks which row starts highlighted, and the
 * player can always override it. What writes `users.country_code` is the prefix of
 * the phone number they actually verified — see the design doc §5.3.
 *
 * Precedence:
 *
 * 1. **The player's selection**, when it is still an active country. A code that
 *    has since been deactivated is ignored rather than honoured, so a stale
 *    localStorage value cannot pin someone to a country that is no longer open.
 * 2. **The IP suggestion**, likewise only when it names an active country. CF can
 *    report a market we have not opened; falling through beats inventing one.
 * 3. **The first active country.** The server orders by `display_order` (V226), so
 *    this is an operator decision rather than an alphabetical accident. No BR
 *    preference here — hardcoding one country reads as arbitrary once a third
 *    market opens.
 * 4. **`BR_FALLBACK`**, only when there is no list at all — a lookup failure, not a
 *    normal path. `countryStore.loaded` stays false there and the register screen
 *    says so.
 */
export function effectiveCountry(
  countries: CountryConfig[],
  selectedCode: string | null | undefined,
  suggestedCode?: string | null,
): CountryConfig {
  if (!countries.length) return BR_FALLBACK;
  for (const code of [selectedCode, suggestedCode]) {
    if (!code) continue;
    const chosen = countries.find((row) => row.code === code);
    if (chosen) return chosen;
  }
  return countries[0];
}

/**
 * Whether the player has a country to choose between.
 *
 * Drives whether the selector renders at all: with one active country the field
 * would be a dropdown with a single option, which reads as broken.
 */
export function needsCountryChoice(countries: CountryConfig[]): boolean {
  return countries.length > 1;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function textMap(value: unknown): Record<string, string> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const entry = text(raw);
    if (entry) out[key] = entry;
  }
  return out;
}
